<div
    class="flex flex-col gap-4"
    data-testid="menu-item-inspector"
    [attr.inert]="locked() ? '' : null"
>
    @if (!item()) {
        <p class="text-sm text-muted-color m-0">
            {{ 'menu-items-editor.select-item-hint' | translate }}
        </p>
    } @else {
        <div class="grid grid-cols-1 gap-3">
            @if (!isMenuType) {
                <div class="flex flex-col gap-1">
                    <phx-labeled-data [label]="'common.title' | translate">
                        <input
                            type="text"
                            pInputText
                            class="w-full"
                            [ngModel]="item()!.title ?? ''"
                            (ngModelChange)="onTitleChange($event)"
                            data-testid="menu-item-title"
                        />
                    </phx-labeled-data>
                </div>
            }

            <div class="flex flex-col gap-1">
                <phx-labeled-data [label]="'common.type' | translate">
                    <p-select
                        [ngModel]="item()!.type ?? 'simple'"
                        (ngModelChange)="onTypeChange($event)"
                        [options]="typeOptions"
                        optionLabel="label"
                        optionValue="value"
                        [appendTo]="'body'"
                        styleClass="w-full"
                        data-testid="menu-item-type"
                    />
                </phx-labeled-data>
            </div>
            @if (isSimpleType) {
                <div class="flex flex-col gap-1">
                    <phx-labeled-data [label]="'menu-items-editor.link' | translate">
                        <input
                            type="text"
                            pInputText
                            class="w-full"
                            [ngModel]="item()!.link ?? ''"
                            (ngModelChange)="onLinkChange($event)"
                            data-testid="menu-item-link"
                        />
                    </phx-labeled-data>
                </div>
            }

            @if (isCustomEntityType) {
                <div class="flex flex-col gap-1">
                    <phx-labeled-data [label]="'menu-items-editor.custom-entity' | translate">
                        <phx-select
                            [items]="customEntityOptions"
                            optionLabel="label"
                            optionValue="value"
                            [value]="item()!.customEntityIdentifier ?? null"
                            (onModelChange)="onCustomEntityChange($event)"
                            [searchable]="true"
                            [placeholder]="
                                'menu-items-editor.custom-entity-placeholder' | translate
                            "
                            [dataTestId]="'menu-item-custom-entity'"
                        />
                    </phx-labeled-data>
                </div>
            }

            @if (isMenuType) {
                <div class="flex flex-col gap-1">
                    <phx-labeled-data [label]="'menu-items-editor.menu-reference' | translate">
                        <app-menu-drop-down
                            [autoloadItems]="true"
                            [(value)]="selectedReferencedMenu"
                            (valueChange)="onReferencedMenuChange($event)"
                            [placeholder]="
                                'menu-items-editor.menu-reference-placeholder' | translate
                            "
                            data-testid="menu-item-menu-reference"
                        />
                    </phx-labeled-data>
                </div>
            }

            @if (supportsIcon) {
                <div class="flex flex-col gap-1">
                    <phx-labeled-data
                        [label]="'common.icon' | translate"
                        [hint]="'menu-items-editor.icon-hint' | translate"
                    >
                        <div class="flex items-center gap-2">
                            <span
                                class="inline-flex items-center justify-center w-8 h-8 shrink-0 text-lg"
                                aria-hidden="true"
                            >
                                @if (item()!.icon) {
                                    <i [class]="item()!.icon!"></i>
                                }
                            </span>
                            <input
                                type="text"
                                pInputText
                                class="w-full min-w-0"
                                [ngModel]="item()!.icon ?? ''"
                                (ngModelChange)="onIconChange($event)"
                                data-testid="menu-item-icon"
                            />
                        </div>
                    </phx-labeled-data>
                </div>
            }

            <div class="flex flex-col gap-1">
                <phx-labeled-data [label]="'menu-items-editor.key' | translate">
                    <input
                        type="text"
                        pInputText
                        class="w-full"
                        [ngModel]="item()!.key ?? ''"
                        (ngModelChange)="onKeyChange($event)"
                        data-testid="menu-item-key"
                    />
                </phx-labeled-data>
            </div>

            <phx-checkbox
                [ngModel]="!item()!.hidden"
                (ngModelChange)="onVisibleInSidebarChange($event)"
                [label]="'menu-items-editor.visible-in-sidebar' | translate"
                data-testid="menu-item-visible-in-sidebar"
            />

            @if (isNavigableLeaf) {
                <phx-checkbox
                    [ngModel]="!!item()!.allowFavorite"
                    (ngModelChange)="onAllowFavoriteChange($event)"
                    [label]="'menu-items-editor.allow-favorite' | translate"
                    data-testid="menu-item-allow-favorite"
                />
            }

            <div class="flex flex-col gap-1">
                <phx-labeled-data
                    [label]="'menu-items-editor.requires-permission' | translate"
                    [hint]="'menu-items-editor.requires-permission-hint' | translate"
                >
                    <phx-select
                        [multiple]="true"
                        [items]="permissionOptions"
                        [value]="item()!.requiresPermission ?? []"
                        (onModelChange)="onPermissionsChange($event)"
                        [searchable]="true"
                        [virtualScroll]="true"
                        [placeholder]="'menu-items-editor.permissions-placeholder' | translate"
                        [dataTestId]="'menu-item-permissions'"
                    />
                </phx-labeled-data>
            </div>

            <div class="flex flex-col gap-1">
                <phx-labeled-data
                    [label]="'menu-items-editor.required-role-ids' | translate"
                    [hint]="'menu-items-editor.required-role-ids-hint' | translate"
                >
                    <app-role-drop-down
                        [multiple]="true"
                        [autoloadItems]="true"
                        [(value)]="selectedRoles"
                        (valueChange)="onSelectedRolesChange($event)"
                        [placeholder]="'menu-items-editor.roles-placeholder' | translate"
                        data-testid="menu-item-roles"
                    />
                </phx-labeled-data>
            </div>

            @if (!isMenuType && !isHeaderType) {
                <div
                    class="border-t border-surface-200 dark:border-surface-700 pt-3 grid grid-cols-1 gap-3"
                >
                    <span class="text-sm font-semibold">{{
                        'menu-items-editor.badge-section' | translate
                    }}</span>
                    <div class="flex flex-col gap-1">
                        <phx-labeled-data [label]="'menu-items-editor.badge-text' | translate">
                            <input
                                type="text"
                                pInputText
                                class="w-full"
                                [ngModel]="badgeText"
                                (ngModelChange)="onBadgeTextChange($event)"
                                data-testid="menu-item-badge-text"
                            />
                        </phx-labeled-data>
                    </div>
                    <div class="flex flex-col gap-1">
                        <phx-labeled-data
                            [label]="'menu-items-editor.badge-class' | translate"
                            [hint]="'menu-items-editor.badge-class-hint' | translate"
                        >
                            <input
                                type="text"
                                pInputText
                                class="w-full"
                                [ngModel]="badgeClass"
                                (ngModelChange)="onBadgeClassChange($event)"
                                data-testid="menu-item-badge-class"
                            />
                        </phx-labeled-data>
                    </div>
                </div>
            }
        </div>
    }
</div>
